import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useEffect } from 'react';
import { ModalWindow, useLocalizeMessage } from 'libs.nucleus';
import { AddEditMeasureStep1 } from './add_edit_measure_step_1.component';
import { AddEditMeasureStep2 } from './add_edit_measure_step_2.component';
import { StepFooter } from './step_footer.component';
import { useStepNavigation } from './use_step_navigation.hook';
const TOTAL_STEPS = 2;
export const AddEditMeasureModal = ({ isOpen, onClose, onSave, dataView, isLoading = false, isEditMode = false, editingMeasureId = null, isReadOnly = false, }) => {
    const translate = useLocalizeMessage();
    // Parse SQL to extract function and dimension if it matches the predefined pattern "function(dimension)"
    // This is used as a fallback for legacy measures that don't have meta.isPredefined
    const parseMeasureSQL = useCallback((sql) => {
        if (!sql) {
            return null;
        }
        // Pattern: function_name(dimension_value) or function_name(DISTINCT dimension_value)
        const pattern = /^([a-z]+)\((?:distinct\s+)?(?:distinct\s*\()?\s*([^)]+?)\s*\)?\)$/i;
        const match = sql.trim().match(pattern);
        if (match) {
            const [, functionName, dimensionValue] = match;
            const functionMap = {
                count: 'Count',
                sum: 'Sum',
                avg: 'Average',
                average: 'Average',
                min: 'Min',
                max: 'Max',
            };
            const capitalizedFunction = functionMap[functionName.toLowerCase()];
            if (capitalizedFunction) {
                return {
                    function: capitalizedFunction,
                    dimension: dimensionValue.trim(),
                };
            }
        }
        return null;
    }, []);
    // Load existing measure data when editing
    const existingMeasure = useMemo(() => {
        if (!isEditMode || !editingMeasureId || !dataView.data?.measures?.[editingMeasureId]) {
            return null;
        }
        const measure = dataView.data.measures[editingMeasureId];
        const sql = measure.sql || '';
        // Check meta.isPredefined flag first, fallback to SQL parsing for legacy measures
        const isPredefined = measure.meta?.isPredefined;
        if (isPredefined === true) {
            // Use the isPredefined flag - parse SQL to extract function and dimension for dropdown mode
            const parsed = parseMeasureSQL(sql);
            return {
                name: measure.title || editingMeasureId,
                description: measure.description || '',
                calculationType: 'DROPDOWN',
                sql,
                measureFunction: parsed?.function,
                dimension: parsed?.dimension,
                type: measure.type || 'number',
            };
        }
        else if (isPredefined === false) {
            // Explicitly set to SQL editor mode
            return {
                name: measure.title || editingMeasureId,
                description: measure.description || '',
                calculationType: 'SQL',
                sql,
                measureFunction: undefined,
                dimension: undefined,
                type: measure.type || 'number',
            };
        }
        // Fallback for legacy measures without meta.isPredefined: try parsing SQL
        const parsed = parseMeasureSQL(sql);
        if (parsed) {
            return {
                name: measure.title || editingMeasureId,
                description: measure.description || '',
                calculationType: 'DROPDOWN',
                sql,
                measureFunction: parsed.function,
                dimension: parsed.dimension,
                type: measure.type || 'number',
            };
        }
        // Default to SQL editor mode
        return {
            name: measure.title || editingMeasureId,
            description: measure.description || '',
            calculationType: (sql ? 'SQL' : 'DROPDOWN'),
            sql,
            measureFunction: undefined,
            dimension: undefined,
            type: measure.type || 'number',
        };
    }, [isEditMode, editingMeasureId, dataView.data?.measures, parseMeasureSQL]);
    const [formData, setFormData] = useState(() => {
        if (existingMeasure) {
            return existingMeasure;
        }
        return {
            name: '',
            description: '',
            calculationType: 'DROPDOWN',
            type: 'number',
        };
    });
    // Track whether the query has been run successfully in step 2
    const [isStep2QueryValid, setIsStep2QueryValid] = useState(false);
    // Update form data when existing measure changes
    useEffect(() => {
        if (existingMeasure) {
            setFormData(existingMeasure);
        }
        else {
            setFormData({
                name: '',
                description: '',
                calculationType: 'DROPDOWN',
                type: 'number',
            });
        }
    }, [existingMeasure]);
    const normalizeTitle = useCallback((value) => value.trim().toLowerCase(), []);
    const isDuplicateTitle = useMemo(() => {
        const candidate = formData.name?.trim();
        if (!candidate) {
            return false;
        }
        const normalizedCandidate = normalizeTitle(candidate);
        const dimensions = dataView.data?.dimensions || {};
        const measures = dataView.data?.measures || {};
        const dimensionTitleExists = Object.values(dimensions).some((dimension) => {
            const title = (dimension.title || '').trim();
            return !!title && normalizeTitle(title) === normalizedCandidate;
        });
        if (dimensionTitleExists) {
            return true;
        }
        return Object.entries(measures).some(([key, measure]) => {
            if (isEditMode && editingMeasureId && key === editingMeasureId) {
                return false;
            }
            const title = (measure.title || '').trim();
            return !!title && normalizeTitle(title) === normalizedCandidate;
        });
    }, [formData.name, dataView.data?.dimensions, dataView.data?.measures, isEditMode, editingMeasureId, normalizeTitle]);
    const nameError = useMemo(() => {
        if (!formData.name?.trim() || !isDuplicateTitle) {
            return '';
        }
        return translate('A dimension or measure with this name already exists');
    }, [formData.name, isDuplicateTitle, translate]);
    const isStep1Valid = useMemo(() => {
        return !!formData.name?.trim() && !isDuplicateTitle;
    }, [formData.name, isDuplicateTitle]);
    const stepValidations = useMemo(() => ({
        1: () => isStep1Valid,
    }), [isStep1Valid]);
    const { currentStep, isFirstStep, isLastStep, handleNext, handlePrevious } = useStepNavigation({
        totalSteps: TOTAL_STEPS,
        stepValidations,
    });
    const handleFormChange = useCallback((data) => {
        setFormData((prev) => ({ ...prev, ...data }));
    }, []);
    const handleQueryValidationChange = useCallback((isValid) => {
        setIsStep2QueryValid(isValid);
    }, []);
    const handleClose = useCallback(() => {
        setFormData({
            name: '',
            description: '',
            calculationType: 'DROPDOWN',
            type: 'number',
        });
        setIsStep2QueryValid(false);
        onClose();
    }, [onClose]);
    const handleCreate = useCallback(async () => {
        if (!formData.name || !formData.calculationType) {
            return;
        }
        const measure = {
            name: formData.name,
            description: formData.description,
            calculationType: formData.calculationType,
            sql: formData.sql,
            measureFunction: formData.measureFunction,
            dimension: formData.dimension,
            type: formData.type || 'number',
        };
        await onSave(measure);
        handleClose();
    }, [formData, onSave, handleClose]);
    const continueButton = {
        label: translate('Continue'),
        onClick: handleNext,
        disabled: !isReadOnly && currentStep === 1 && !isStep1Valid,
    };
    const createButton = {
        label: isEditMode ? translate('Save') : translate('Create'),
        onClick: handleCreate,
        disabled: isLoading || !formData.name || !formData.calculationType || !isStep2QueryValid,
    };
    const getPrimaryActionButton = () => {
        if (isReadOnly && isLastStep) {
            return undefined;
        }
        return isLastStep ? createButton : continueButton;
    };
    const getSecondaryActionButtons = () => {
        const closeOrCancelLabel = isReadOnly ? translate('Close') : translate('Cancel');
        if (isFirstStep) {
            return [{ label: closeOrCancelLabel, onClick: handleClose }];
        }
        return [
            { label: closeOrCancelLabel, onClick: handleClose },
            { label: translate('Back'), onClick: handlePrevious },
        ];
    };
    const getModalTitle = () => {
        if (isReadOnly) {
            return translate('View measure');
        }
        return isEditMode ? translate('Edit measure') : translate('Create measure');
    };
    if (!isOpen) {
        return null;
    }
    return (_jsxs(ModalWindow, { title: getModalTitle(), subtitle: dataView.name, isOpen: true, closeWindow: handleClose, footerPrimaryActionButton: getPrimaryActionButton(), footerSecondaryActionButtons: getSecondaryActionButtons(), footerCenterContent: _jsx(StepFooter, { currentStep: currentStep, totalSteps: TOTAL_STEPS }), width: 'full', children: [currentStep === 1 && (_jsx(AddEditMeasureStep1, { formData: formData, onFormChange: handleFormChange, isEditMode: isEditMode, isReadOnly: isReadOnly, nameError: nameError })), currentStep === 2 && (_jsx(AddEditMeasureStep2, { formData: formData, onFormChange: handleFormChange, dataView: dataView, isEditMode: isEditMode, isReadOnly: isReadOnly, onQueryValidationChange: handleQueryValidationChange }))] }));
};
